import * as React from 'react';
import { BaseProps, IAddonsProps } from '../common/props';
import classNames from 'classnames';
import * as Classes from '../common/classes';
import { useEffect, useRef, useState } from 'react';
import { InputAddons } from '../input-addons/InputAddons';
import { InputGroup, InputGroupProps } from '../input-group/InputGroup';

export interface DayMonth {
  day: number;
  month: number;
}

export interface IDayMonthInputProps
  extends BaseProps,
    IAddonsProps,
    Omit<
      InputGroupProps,
      | 'onChange'
      | 'asyncControl'
      | 'rightElement'
      | 'value'
      | 'onBlur'
      | 'type'
      | 'defaultValue'
    > {
  onChange?: (value: DayMonth | undefined) => void;
  onBlur?: (value: DayMonth | undefined) => void;
  value?: DayMonth;
  readOnly?: boolean;
  fullWidth?: boolean;
}

export type DayMonthInputProps = IDayMonthInputProps;

/**
 * Komponenta pro editaci dne a měsíce bez ohledu na rok.
 */
export const DayMonthInput: React.FC<DayMonthInputProps> = ({
  className,
  onChange,
  onBlur,
  value,
  addonAfter,
  addonAfterCompact,
  disabled,
  readOnly,
  fullWidth,
  dataTest,
  autoFocus,
  ...other
}) => {
  const currEdited = useRef<DayMonth | undefined>(value);

  const formatValue = (value?: DayMonth): string => {
    if (!value || !value.day || !value.month) {
      return '';
    }

    return `${('0' + value.day).slice(-2)}.${('0' + value.month).slice(-2)}.`;
  };

  const parseValue = (value: string): DayMonth | undefined => {
    if (!value) {
      return undefined;
    }

    const updatedValue = value.replace(/,/g, ' ').replace(/\s\s+/g, ' ');
    const rr = /[^\d]*([\d]+)[^\d]*([\d]*)/g;
    const parts = rr.exec(updatedValue);
    if (parts) {
      const day = parts[1];
      const month = parts[2] || `${new Date().getMonth() + 1}`;

      let dayNum = parseInt(day);
      let monthNum = parseInt(month);

      if (monthNum < 1) {
        monthNum = 1;
      } else if (monthNum > 12) {
        monthNum = 12;
      }

      switch (monthNum) {
        case 1:
        case 3:
        case 5:
        case 7:
        case 8:
        case 10:
        case 12:
          if (dayNum < 1) {
            dayNum = 1;
          } else if (dayNum > 31) {
            dayNum = 31;
          }
          break;
        case 2:
          if (dayNum < 1) {
            dayNum = 1;
          } else if (dayNum > 28) {
            dayNum = 28;
          }
          break;
        case 4:
        case 6:
        case 9:
        case 11:
          if (dayNum < 1) {
            dayNum = 1;
          } else if (dayNum > 30) {
            dayNum = 30;
          }
          break;
      }

      let newValue = {
        day: dayNum,
        month: monthNum,
      };

      return newValue;
    } else {
      return undefined;
    }
  };

  const [text, setText] = useState<string>(() => formatValue(value));

  useEffect(() => {
    if (value !== currEdited.current) {
      const newTextValue = formatValue(value);
      setText(newTextValue);
    }
  }, [value]);

  const compareValues = (v1?: DayMonth, v2?: DayMonth): boolean => {
    if (!v1 && !v2) {
      return true;
    } else if (!v1 || !v2) {
      return false;
    }

    return v1.day === v2.day && v1.month === v2.month;
  };

  const handleBlur = () => {
    const newText = formatValue(value);
    if (newText !== text) {
      setText(newText);
    }
    onBlur?.(value);
  };

  const handleChange = (e: any) => {
    let str = e.target.value;

    const newValue = parseValue(str);
    if (!compareValues(newValue, value)) {
      currEdited.current = newValue;
      onChange?.(newValue);
    }

    setText(str || '');
  };

  return (
    <InputAddons
      limitedSize
      addonAfter={addonAfter}
      addonAfterCompact={addonAfterCompact}
    >
      <InputGroup
        data-test={dataTest ? `input-${dataTest}` : undefined}
        className={classNames(
          className,
          Classes.FIELD_INPUT,
          Classes.DAY_MONTH_INPUT,
          {
            [Classes.FIELD_INPUT_DISABLED]: disabled,
            [Classes.FIELD_INPUT_READONLY]: readOnly,
            [Classes.LIMIT_DATE_TIME_SIZE]: !fullWidth,
          }
        )}
        onChange={handleChange}
        onBlur={handleBlur}
        value={text}
        {...other}
        disabled={disabled}
        readOnly={readOnly}
        autoFocus={autoFocus}
      />
    </InputAddons>
  );
};
